/**
 * Route around a keep-out instead of refusing to fly.
 *
 * Blocking an upload that breaches a zone is honest but unhelpful: the operator
 * still has to redraw the leg by hand, around a boundary the app can already
 * see. This finds the way around.
 *
 * A* over a visibility graph, which is the right shape for this problem: the
 * shortest path around polygonal obstacles always bends at their corners, so
 * the only nodes worth considering are the zone vertices plus the two ends.
 * That is a handful of nodes for a realistic number of zones, rather than the
 * hundreds of thousands a grid search would visit for the same answer.
 *
 * Corners are pushed outward by a buffer first. A path that grazes the exact
 * vertex of a keep-out is inside it as far as any sane operator is concerned,
 * and floating-point comparisons on the boundary are a coin toss.
 */

import { pointInPolygon, distanceM } from '@/map/zoneGeometry';
import type { Zone } from '@/stores/missionStore';
import { MavCmd, type Waypoint } from '@/types/domain';

export interface Point {
  lat: number;
  lon: number;
}

/** How far outside a zone the route is routed. Metres. */
export const ZONE_BUFFER_M = 40;
/** Segments used to approximate a circular zone. */
const CIRCLE_SEGMENTS = 24;
const METRES_PER_DEG_LAT = 111_320.0;

function metresToLat(m: number): number {
  return m / METRES_PER_DEG_LAT;
}

function metresToLon(m: number, atLat: number): number {
  return m / (METRES_PER_DEG_LAT * Math.max(0.01, Math.cos((atLat * Math.PI) / 180)));
}

/**
 * A zone as a ring of points, pushed out by `bufferM`.
 *
 * Circles become polygons here rather than staying analytic: one obstacle
 * representation means one intersection test, and 24 segments is well under
 * the error the buffer already absorbs.
 */
export function bufferedRing(zone: Zone, bufferM: number = ZONE_BUFFER_M): Point[] {
  if (zone.shape.type === 'circle') {
    const { lat, lon, radiusM } = zone.shape;
    const r = radiusM + bufferM;
    const ring: Point[] = [];
    for (let i = 0; i < CIRCLE_SEGMENTS; i += 1) {
      const angle = (i / CIRCLE_SEGMENTS) * 2 * Math.PI;
      ring.push({
        lat: lat + metresToLat(r) * Math.sin(angle),
        lon: lon + metresToLon(r, lat) * Math.cos(angle),
      });
    }
    return ring;
  }

  const points = zone.shape.points;
  if (points.length === 0) return [];
  // Push each vertex away from the centroid. Crude next to a true polygon
  // offset, and adequate: the buffer only has to clear the corner, and a
  // convex-ish operating area is what anyone actually draws.
  const cLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  const cLon = points.reduce((sum, p) => sum + p.lon, 0) / points.length;
  return points.map((p) => {
    const dLat = p.lat - cLat;
    const dLon = p.lon - cLon;
    const mag = Math.hypot(dLat, dLon);
    if (mag === 0) return p;
    return {
      lat: p.lat + (dLat / mag) * metresToLat(bufferM),
      lon: p.lon + (dLon / mag) * metresToLon(bufferM, p.lat),
    };
  });
}

function orientation(a: Point, b: Point, c: Point): number {
  const v = (b.lon - a.lon) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lon - a.lon);
  if (Math.abs(v) < 1e-12) return 0;
  return v > 0 ? 1 : -1;
}

function onSegment(a: Point, b: Point, p: Point): boolean {
  return (
    Math.min(a.lat, b.lat) <= p.lat &&
    p.lat <= Math.max(a.lat, b.lat) &&
    Math.min(a.lon, b.lon) <= p.lon &&
    p.lon <= Math.max(a.lon, b.lon)
  );
}

export function segmentsCross(a1: Point, a2: Point, b1: Point, b2: Point): boolean {
  const o1 = orientation(a1, a2, b1);
  const o2 = orientation(a1, a2, b2);
  const o3 = orientation(b1, b2, a1);
  const o4 = orientation(b1, b2, a2);
  if (o1 !== o2 && o3 !== o4) return true;
  // Collinear touches count: a leg that runs along an edge is on the boundary.
  if (o1 === 0 && onSegment(a1, a2, b1)) return true;
  if (o2 === 0 && onSegment(a1, a2, b2)) return true;
  if (o3 === 0 && onSegment(b1, b2, a1)) return true;
  if (o4 === 0 && onSegment(b1, b2, a2)) return true;
  return false;
}

/**
 * A strict crossing: the segments genuinely pass through each other.
 *
 * Touching is deliberately not a crossing. Every useful edge in the visibility
 * graph either ends on a corner or runs along the boundary between two
 * adjacent corners, so counting a touch as a crossing blocks every route round
 * the obstacle and A* correctly reports that nothing is reachable.
 */
function properlyCross(a1: Point, a2: Point, b1: Point, b2: Point): boolean {
  const o1 = orientation(a1, a2, b1);
  const o2 = orientation(a1, a2, b2);
  const o3 = orientation(b1, b2, a1);
  const o4 = orientation(b1, b2, a2);
  if (o1 === 0 || o2 === 0 || o3 === 0 || o4 === 0) return false;
  return o1 !== o2 && o3 !== o4;
}

/** The ring pulled very slightly toward its middle. */
function shrunk(ring: Point[]): Point[] {
  const cLat = ring.reduce((s, p) => s + p.lat, 0) / ring.length;
  const cLon = ring.reduce((s, p) => s + p.lon, 0) / ring.length;
  const k = 0.999;
  return ring.map((p) => ({
    lat: cLat + (p.lat - cLat) * k,
    lon: cLon + (p.lon - cLon) * k,
  }));
}

/**
 * Does the leg a→b enter this ring?
 *
 * Containment is tested against a hair-shrunk copy. A leg running along the
 * boundary has its samples exactly on the edge, where a ray cast is a coin
 * toss; shrinking puts them reliably outside, which is the answer that lets a
 * route travel around the obstacle instead of being walled in by it.
 */
export function legEntersRing(a: Point, b: Point, ring: Point[]): boolean {
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    if (properlyCross(a, b, ring[j]!, ring[i]!)) return true;
  }
  const inner = shrunk(ring);
  for (const t of [0.25, 0.5, 0.75]) {
    const lat = a.lat + (b.lat - a.lat) * t;
    const lon = a.lon + (b.lon - a.lon) * t;
    if (pointInPolygon(lat, lon, inner)) return true;
  }
  return false;
}

export interface PathResult {
  ok: boolean;
  /** Start and end included. Two points means the direct leg was already clear. */
  path: Point[];
  /** Why no path could be produced. */
  reason?: string;
}

/**
 * A way from `start` to `end` that stays out of every keep-out.
 *
 * Keep-in zones are not obstacles and are ignored here: staying inside one is a
 * different constraint, already reported by routeViolations, and treating its
 * boundary as a wall to route around would produce nonsense.
 */
export function findPath(
  start: Point,
  end: Point,
  zones: readonly Zone[],
  { bufferM = ZONE_BUFFER_M }: { bufferM?: number } = {},
): PathResult {
  const rings = zones
    .filter((z) => z.kind === 'keep-out')
    .map((z) => bufferedRing(z, bufferM))
    .filter((ring) => ring.length >= 3);

  if (rings.length === 0) return { ok: true, path: [start, end] };

  // An endpoint inside a keep-out has no legal route: there is no way out that
  // is not already a breach. Saying so beats returning a path through the wall.
  for (const ring of rings) {
    if (pointInPolygon(start.lat, start.lon, ring)) {
      return { ok: false, path: [], reason: 'The start of this leg is inside a keep-out zone.' };
    }
    if (pointInPolygon(end.lat, end.lon, ring)) {
      return { ok: false, path: [], reason: 'The end of this leg is inside a keep-out zone.' };
    }
  }

  const blocked = (a: Point, b: Point): boolean => rings.some((ring) => legEntersRing(a, b, ring));
  if (!blocked(start, end)) return { ok: true, path: [start, end] };

  // Nodes: the two ends, plus every buffered corner. The shortest way around a
  // polygon bends at its corners, so nothing else can be on the optimal path.
  const nodes: Point[] = [start, end, ...rings.flat()];
  const START = 0;
  const END = 1;

  const heuristic = (i: number): number =>
    distanceM(nodes[i]!.lat, nodes[i]!.lon, nodes[END]!.lat, nodes[END]!.lon);

  const gScore = new Map<number, number>([[START, 0]]);
  const fScore = new Map<number, number>([[START, heuristic(START)]]);
  const cameFrom = new Map<number, number>();
  const open = new Set<number>([START]);
  const closed = new Set<number>();

  while (open.size > 0) {
    let current = -1;
    let best = Infinity;
    for (const node of open) {
      const score = fScore.get(node) ?? Infinity;
      if (score < best) {
        best = score;
        current = node;
      }
    }
    if (current === END) break;
    open.delete(current);
    closed.add(current);

    for (let next = 0; next < nodes.length; next += 1) {
      if (next === current || closed.has(next)) continue;
      if (blocked(nodes[current]!, nodes[next]!)) continue;
      const step = distanceM(
        nodes[current]!.lat,
        nodes[current]!.lon,
        nodes[next]!.lat,
        nodes[next]!.lon,
      );
      const tentative = (gScore.get(current) ?? Infinity) + step;
      if (tentative >= (gScore.get(next) ?? Infinity)) continue;
      cameFrom.set(next, current);
      gScore.set(next, tentative);
      fScore.set(next, tentative + heuristic(next));
      open.add(next);
    }
  }

  if (!cameFrom.has(END)) {
    return { ok: false, path: [], reason: 'No way around the keep-out zones on this leg.' };
  }

  const path: Point[] = [];
  for (let at: number | undefined = END; at !== undefined; at = cameFrom.get(at)) {
    path.push(nodes[at]!);
    if (at === START) break;
  }
  return { ok: true, path: path.reverse() };
}

/**
 * Rewrite a route so no leg passes through a keep-out.
 *
 * Only the offending legs are touched. A route with one bad leg out of ten
 * comes back with nine identical legs and a detour, so an operator can see what
 * changed rather than having their plan silently rewritten.
 *
 * Inserted points inherit the altitude of the leg they belong to: a detour is a
 * horizontal decision, and quietly changing the height an aircraft flies at is
 * not something a reroute button should do.
 */
export function rerouteAround(
  waypoints: readonly Waypoint[],
  zones: readonly Zone[],
): { waypoints: Waypoint[]; changed: boolean; reason?: string } {
  const positional = (wp: Waypoint): boolean => !(wp.lat === 0 && wp.lon === 0);
  const out: Waypoint[] = [];
  let changed = false;

  for (let i = 0; i < waypoints.length; i += 1) {
    const current = waypoints[i]!;
    out.push(current);
    const next = waypoints[i + 1];
    if (!next || !positional(current) || !positional(next)) continue;

    const result = findPath(
      { lat: current.lat, lon: current.lon },
      { lat: next.lat, lon: next.lon },
      zones,
    );
    if (!result.ok) return { waypoints: [...waypoints], changed: false, reason: result.reason };
    // First and last are the waypoints themselves; only the bend is new.
    const bend = result.path.slice(1, -1);
    for (const point of bend) {
      changed = true;
      out.push({ ...next, lat: point.lat, lon: point.lon, command: MavCmd.NavWaypoint });
    }
  }

  return { waypoints: out, changed };
}
